import * as React from 'react';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';

import {StackLegacy} from '../layout/stack/stack-legacy';
import type {StackSpacing} from '../layout/stack/stack-legacy';

import {BreadcrumbValue} from './breadcrumb-value';
import {CircleNumber} from './circle-number';

import styles from './numbered-breadcrumbs.module.css';

export interface Breadcrumb {
    id: string;
    title: string;
    subtitle?: string | React.ReactNode;
    selectable?: boolean;
}

interface Props {
    breadcrumbs: Breadcrumb[];
    selectedValue?: string;
    onSelectValue?: (arg1: string) => void;
    isHorizontal?: boolean;
    spacing?: StackSpacing;
    useCompleteSubtitle?: boolean;
    isLastBreadcrumbComplete?: boolean;
    titleSize?: 'small' | 'normal';
    subtitleSize?: 'mini' | 'small' | 'normal';
    subtitleColor?: 'light' | 'medium';
    onlyDisplaySubtitleWhenCompleted?: boolean;
    selectedItemRef?: {
        current: HTMLElement | null;
    };
    hasUnsavedBillingChanges?: boolean;
}

export function NumberedBreadcrumbs(props: Props) {
    const {
        isHorizontal = true,
        spacing = 8,
        titleSize = 'small',
        subtitleSize = 'small',
        onSelectValue,
    } = props;

    const [showWarning, setShowWarning] = React.useState<boolean>(false);
    const [clickedBreadcrumbId, setClickedBreadcrumbId] = React.useState<string | null>(null);

    const selectedIndex = props.breadcrumbs.findIndex(
        (breadcrumb) => breadcrumb.id === props.selectedValue
    );

    const handleNumberClick = (breadcrumbId: string, e: React.SyntheticEvent<any>) => {
        setClickedBreadcrumbId(breadcrumbId);
        if (props.hasUnsavedBillingChanges) {
            setShowWarning(true);

            return;
        }

        onSelectValue?.(breadcrumbId);
        // This is to prevent the button text from
        // remaining underlined after a click, but still
        // show the underline on a tab focus
        e.currentTarget.blur();
    };

    const handleWarningConfirm = () => {
        setShowWarning(false);
        onSelectValue?.(clickedBreadcrumbId || '');
    };

    return (
        <>
            <StackLegacy spacing={spacing} isHorizontal={isHorizontal} alignCenter={isHorizontal}>
                {props.breadcrumbs.map((breadcrumb, index) => {
                    let state = 'initial';
                    if (
                        index < selectedIndex ||
                        (index === props.breadcrumbs.length - 1 && props.isLastBreadcrumbComplete)
                    ) {
                        state = 'past';
                    } else if (selectedIndex === index) {
                        state = 'selected';
                    }

                    const subtitle = getSubtitle(
                        state,
                        breadcrumb.subtitle,
                        props.useCompleteSubtitle,
                        props.onlyDisplaySubtitleWhenCompleted
                    );

                    const content = (
                        <div className='flex align-center flex-grow'>
                            <div className={styles['circle-container']}>
                                {/* @ts-expect-error */}
                                <CircleNumber count={index + 1} state={state} />
                            </div>
                            <BreadcrumbValue
                                title={breadcrumb.title}
                                subtitle={subtitle}
                                // @ts-expect-error
                                state={state}
                                isDisabled={!breadcrumb.selectable}
                                preventUnderline={!onSelectValue}
                                titleSize={titleSize}
                                subtitleSize={subtitleSize}
                                subtitleColor={props.subtitleColor}
                            />
                        </div>
                    );

                    if (breadcrumb.selectable && onSelectValue) {
                        return (
                            <button
                                key={breadcrumb.id}
                                onClick={(e) => {
                                    handleNumberClick(breadcrumb.id, e);
                                }}
                                // @ts-expect-error
                                ref={state === 'selected' ? props.selectedItemRef : null}
                                className={styles['option-button']}
                            >
                                {content}
                            </button>
                        );
                    } else {
                        return (
                            <div
                                key={breadcrumb.id}
                                // @ts-expect-error
                                ref={state === 'selected' ? props.selectedItemRef : null}
                                className={
                                    state === 'selected'
                                        ? styles['option--selected']
                                        : styles.option
                                }
                            >
                                {content}
                            </div>
                        );
                    }
                })}
            </StackLegacy>
            {showWarning && (
                <ConfirmationDialog
                    title='Unsaved Billing Information'
                    message='Are you sure you want to leave this page without saving?'
                    onConfirm={handleWarningConfirm}
                    onCancel={() => setShowWarning(false)}
                    // @ts-expect-error
                    isOpen={showWarning}
                />
            )}
        </>
    );
}

function getSubtitle(
    state: string,
    subtitle?: string | React.ReactNode,
    useCompleteSubtitle?: boolean,
    onlyDisplaySubtitleWhenCompleted?: boolean
) {
    if (state !== 'past' && onlyDisplaySubtitleWhenCompleted) {
        return null;
    }

    if (state === 'past' && useCompleteSubtitle) {
        return 'Complete!';
    }

    return subtitle;
}
